import { useCallback } from 'react';
import { useGetOrderVariants, useUpdateOrderVariants } from '@/entities/order';
import { getRequestContext } from '@/shared/lib/requestContext';

interface UseFetchVariantsParams {
    chunkId: number;
    variantId: string;
}

export const useVariantsController = ({ chunkId, variantId }: UseFetchVariantsParams) => {
    const { orderId, storeSlug, signature } = getRequestContext();

    const {
        fetchVariants,
        currentVariants,
        isLoading: isLoadingGetVariants,
        error: getVariantsError,
        resetVariants,
    } = useGetOrderVariants();
    const {
        updateOrderVariants,
        isLoading: isLoadingUpdateVariants,
        error: updateVariantsError,
    } = useUpdateOrderVariants();

    const handleFetchVariants = useCallback(
        async ({ selectedId, selectedValue }: { selectedId?: string; selectedValue?: string } = {}) => {
            await fetchVariants({
                order_id: orderId,
                store_slug: storeSlug,
                signature: signature,
                delivery_chunk_id: chunkId,
                original_variant_id: variantId,
                variant_id: currentVariants?.new_variant_id,
                selected_characteristic_id: selectedId,
                selected_characteristic_value: selectedValue,
            });
        },
        [chunkId, orderId, signature, storeSlug, currentVariants?.new_variant_id, fetchVariants, variantId]
    );

    const handleUpdateOrderVariants = useCallback(
        async ({ newVariantId }: { newVariantId: string }) => {
            await updateOrderVariants({
                order_id: orderId,
                store_slug: storeSlug,
                signature: signature,
                delivery_chunk_id: chunkId,
                original_variant_id: variantId,
                new_variant_id: newVariantId,
            });
        },
        [chunkId, orderId, signature, storeSlug, updateOrderVariants, variantId]
    );

    return {
        fetchVariants: handleFetchVariants,
        updateVariants: handleUpdateOrderVariants,
        resetVariants,
        currentVariants,
        isLoading: isLoadingGetVariants || isLoadingUpdateVariants,
        error: getVariantsError || updateVariantsError,
    };
};
